import React, { PureComponent, KeyboardEvent } from 'react';

import {
  extendedTokens,
  standardTokens,
  getLanguageByFileName,
  // @ts-ignore TODO: fix noImplicitAny error here
} from 'monaco-languages-extended';

import { useThemeObserver } from '@atlaskit/tokens';

import { IndentationMode, IndentationSize, RangeSelection } from '../types';
import { buildLineUrlFragment } from '../utils/parse-hash';

import {
  defaultOptions,
  editorOptions,
  readOnlyOptions,
} from './monaco-file.config';
import * as styles from './monaco-file.style';
import ReactMonacoEditor from './monaco-library';
import bitbucketDarkTheme from './monaco-themes/bitbucket-dark-theme.json';
import bitbucketLightTheme from './monaco-themes/bitbucket-light-theme.json';

const LINE_NUM_CLASSNAME = 'line-number';

// Number of lines to leave above the first highlighted/selected line on initial render
const SELECTED_LINE_TOP_BUFFER = 6;

export type MonacoFileProps = {
  filename: string;
  height: string;
  indentationMode?: IndentationMode;
  indentationSize?: IndentationSize;
  isEditing: boolean;
  isHidden: boolean;
  language?: string;
  lineWrap?: boolean;
  onChange: (value: string) => void;
  onClick: (e: MouseEvent) => void;
  onEditorMount: (editor: any, monaco: any) => void;
  onLineNumClick: (e: MouseEvent, lineNum: number) => void;
  selectedRanges: RangeSelection[];
  value: string;
};

enum MonacoTheme {
  LIGHT = 'bitbucket-light',
  DARK = 'bitbucket-dark',
}

type MonacoThemeProps = {
  monacoTheme: MonacoTheme;
};

type Props = MonacoFileProps & MonacoThemeProps;

type State = {
  language: string | null | undefined;
};

class MonacoFile extends PureComponent<Props, State> {
  editor: any;
  editorDomNode: HTMLElement | null | undefined;
  initialEditorOptions: object;
  lineDecorationIds: string[];
  /**
   * A "context key" in the Monaco API:
   *
   * https://microsoft.github.io/monaco-editor/api/interfaces/monaco.editor.istandalonecodeeditor.html#createcontextkey
   *
   * Can be used as an enable/disable condition for custom commands added to the editor.
   * Another example can be found in the interactive Playground docs
   *
   * https://microsoft.github.io/monaco-editor/playground.html#interacting-with-the-editor-adding-a-command-to-an-editor-instance
   */
  monacoConditionIsEditing: any;

  static defaultProps = {
    /* eslint react/default-props-match-prop-types: "warn" */
    isEditing: false,
    isHidden: false,
    lineWrap: false,
    onChange: () => {},
    onClick: () => {},
    onEditorMount: () => {},
    onLineNumClick: () => {},
    selectedRanges: [],
  };

  constructor(props: Props) {
    super(props);

    this.initialEditorOptions = {
      ...defaultOptions,
      ...(props.isEditing ? editorOptions : readOnlyOptions),
      ariaLabel: this.props.filename,
      lineNumbers: this.renderLineNumber,
      wordWrap: props.lineWrap ? 'on' : 'off',
    };
  }

  state = {
    language: this.props.language,
  };

  componentDidUpdate(prevProps: Props) {
    const { isEditing } = this.props;

    if (this.props.selectedRanges !== prevProps.selectedRanges && !isEditing) {
      this.updateSelectedLines(this.props.selectedRanges);
    }

    if (isEditing !== prevProps.isEditing) {
      this.toggleEditMode();
    }

    if (
      this.props.indentationSize !== prevProps.indentationSize ||
      this.props.indentationMode !== prevProps.indentationMode
    ) {
      this.updateIndentation();
    }

    if (this.props.lineWrap !== prevProps.lineWrap) {
      this.updateLineWrap();
    }

    // FIXME: @peterwilliams this condition doesn't make sense based on the types
    // @ts-ignore
    if (this.props.filename !== prevProps.lineWrap) {
      this.updateFilename();
    }
  }

  componentWillUnmount() {
    // @ts-ignore
    window.removeEventListener('keydown', this.handleFind);
    if (this.editorDomNode) {
      this.editorDomNode.removeEventListener('click', this.handleClick);
    }
  }

  editorWillMount = (monaco: any) => {
    monaco.editor.defineTheme('bitbucket-dark', bitbucketDarkTheme);
    monaco.editor.defineTheme('bitbucket-light', bitbucketLightTheme);
  };

  editorDidMount = (editor: any, monaco: any) => {
    const { language } = this.props;

    this.editor = editor;
    this.editorDomNode = this.editor.getDomNode();

    // Disable the command palette when not in "edit" mode
    this.monacoConditionIsEditing = editor.createContextKey('isEditing', false);
    editor.addCommand(monaco.KeyCode.F1, () => {}, '!isEditing');

    // If no syntax highlighter was found, check extended language support
    // library for a potential match, and set syntax if matches
    if (language === 'plaintext') {
      this.setFallbackTokenProvider(monaco);
    }

    // Even if a highlighter was found, check if a matched language has
    // extended tokenization support provided by monaco-lanaguages-extended
    this.setExtendedTokenProvider(monaco);

    // Register global event handler for Ctrl/Cmd+F
    // @ts-ignore
    window.addEventListener('keydown', this.handleFind);

    // Register a click handler for line numbers
    if (this.editorDomNode) {
      this.editorDomNode.addEventListener('click', this.handleClick);
    }

    // Dynamically update the gutter width for line #s based on file LOC
    const lineCount = editor.getModel().getLineCount();
    // the gutter only really fits N-1 characters
    const numDigits = lineCount.toString().length + 1;
    editor.updateOptions({
      lineNumbersMinChars: numDigits,
    });

    this.updateSelectedLines(this.props.selectedRanges, {
      revealRangeAtTop: true,
    });

    this.toggleEditMode();

    // Pass the args to the parent AnnotatedMonacoFile so it can display
    // the source annotations from Connect modules.
    this.props.onEditorMount(editor, monaco);
  };

  handleClick = (e: MouseEvent) => {
    if (!(e.target instanceof HTMLElement)) {
      return;
    }

    const { classList, dataset } = e.target;
    if (classList.contains(LINE_NUM_CLASSNAME) && dataset.lineNum) {
      this.props.onLineNumClick(e, parseInt(dataset.lineNum, 10));
    } else {
      this.props.onClick(e);
    }
  };

  handleFind = (event: KeyboardEvent) => {
    // don't hijack "find" if Monaco is hidden (e.g., when a different editor mode or Connect is active)
    if (this.props.isHidden) {
      return;
    }

    const modKey = /Mac|iPod|iPhone|iPad/.test(navigator.platform)
      ? event.metaKey
      : event.ctrlKey;
    if (modKey && event.key === 'f') {
      event.preventDefault();
      // @ts-ignore
      event.stopImmediatePropagation();
      if (this.editor) {
        this.editor.getAction('actions.find').run();
      }
    }
  };

  /**
   * Monaco's API only offers limited customization of the rendered value, and the most
   * control given is via a function that accepts the line # and returns a string.
   * This is why we aren't using JSX and React here.
   *
   * This is not sanitized so be *very* careful what you include in the returned string
   *
   * Docs: https://microsoft.github.io/monaco-editor/api/interfaces/monaco.editor.ieditorconstructionoptions.html#linenumbers
   */
  renderLineNumber = (lineNum: number) => {
    if (this.props.isEditing) {
      return lineNum;
    }
    const href = buildLineUrlFragment(lineNum);
    return `<a class="${LINE_NUM_CLASSNAME}" data-line-num="${lineNum}" href="#${href}">${lineNum}</a>`;
  };

  setExtendedTokenProvider = (monaco: any) => {
    const lang = this.props.language || '';
    const extLangName = `${lang}-extended`;
    if (Object.keys(extendedTokens).includes(lang)) {
      monaco.languages.register({ id: extLangName });
      monaco.languages.setMonarchTokensProvider(
        extLangName,
        extendedTokens[lang]
      );

      this.setState({ language: extLangName });
    }
  };

  setFallbackTokenProvider = (monaco: any) => {
    // If no token provider was found by monaco, use the file extension to
    // check monaco-languages-extended for support of that language.
    const lang = getLanguageByFileName(this.props.filename);
    if (lang) {
      // If there is a language defined, register it, load the syntax, and
      // change the editor language to the newly defined provider
      monaco.languages.register({ id: lang });
      monaco.languages.setMonarchTokensProvider(lang, standardTokens[lang]);
      this.setState({ language: lang });
    }
  };

  toggleEditMode() {
    const { isEditing } = this.props;
    const newOptions = isEditing ? editorOptions : readOnlyOptions;

    if (this.editor) {
      this.editor.updateOptions(newOptions);
      if (isEditing) {
        this.editor.focus();
      }
    }

    if (this.monacoConditionIsEditing) {
      this.monacoConditionIsEditing.set(isEditing);
    }

    // disable line highlighting when in "edit" mode
    this.updateSelectedLines(isEditing ? [] : this.props.selectedRanges);
  }

  updateFilename() {
    if (this.editor) {
      this.editor.updateOptions({ ariaLabel: this.props.filename });
    }
  }

  updateIndentation() {
    const { indentationMode, indentationSize } = this.props;
    if (this.editor) {
      const { insertSpaces, tabSize } = this.editor.getModel().getOptions();
      // preserve monaco's defaults / "guesses" if the props aren't explicitly set
      this.editor.getModel().updateOptions({
        insertSpaces: indentationMode
          ? indentationMode === 'spaces'
          : insertSpaces,
        tabSize: indentationSize || tabSize,
      });
    }
  }

  updateLineWrap() {
    const { lineWrap } = this.props;
    if (this.editor) {
      this.editor.updateOptions({
        wordWrap: lineWrap ? 'on' : 'off',
        wordWrapMinified: lineWrap,
      });
    }
  }

  updateSelectedLines(
    ranges: RangeSelection[],
    opts: { revealRangeAtTop?: boolean } = {}
  ) {
    if (!this.editor) {
      return;
    }

    if (ranges.length && opts.revealRangeAtTop) {
      const range = ranges[0];
      this.editor.revealRangeAtTop({
        endColumn: 1,
        endLineNumber: range.end || range.start,
        startColumn: 1,
        startLineNumber: Math.max(range.start - SELECTED_LINE_TOP_BUFFER, 1),
      });
    }

    const lineDecorations = ranges.map(range => ({
      options: {
        className: styles.HIGHLIGHTED_LINE_CLASSNAME,
        isWholeLine: true,
      },
      range: {
        endColumn: 1,
        endLineNumber: range.end || range.start,
        startColumn: 1,
        startLineNumber: range.start,
      },
    }));

    this.lineDecorationIds = this.editor.deltaDecorations(
      this.lineDecorationIds || [],
      lineDecorations
    );
  }

  render() {
    return (
      <styles.MonacoWrapper
        height={this.props.height}
        isEditing={this.props.isEditing}
        isHidden={this.props.isHidden}
      >
        <ReactMonacoEditor
          language={this.state.language}
          editorDidMount={this.editorDidMount}
          editorWillMount={this.editorWillMount}
          onChange={this.props.onChange}
          // react-monaco-editor does not re-render or update based on changes to this prop
          options={this.initialEditorOptions}
          theme={this.props.monacoTheme}
          value={this.props.value}
        />
      </styles.MonacoWrapper>
    );
  }
}

const MonacoFileWithTheme = (props: MonacoFileProps): JSX.Element => {
  const { colorMode } = useThemeObserver();

  const monacoTheme: MonacoTheme =
    colorMode === 'dark' ? MonacoTheme.DARK : MonacoTheme.LIGHT;

  return <MonacoFile {...props} monacoTheme={monacoTheme} />;
};

export { MonacoFileWithTheme as MonacoFile };
